import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router';

import { apiGet, apiPatch, apiPost } from '../lib/api';

type InvoiceStatus = 'draft' | 'sent' | 'paid' | 'void' | 'overdue';

interface InvoiceItem {
  id: string;
  item_name: string | null;
  description: string;
  quantity: number;
  unit_price: number;
  amount: number;
  category: string | null;
  provider: string | null;
  model: string | null;
}

interface InvoiceDetail {
  id: string;
  invoice_number: string;
  project_id: string;
  client_id: string;
  status: InvoiceStatus;
  currency: string;
  subtotal: number;
  tax_amount: number;
  total: number;
  issue_date: string;
  due_date: string;
  period_start: string | null;
  period_end: string | null;
  payment_reference: string | null;
  payment_method: string | null;
  stripe_payment_link_url: string | null;
  manual_paid_reason: string | null;
  manual_paid_marked_by: string | null;
  manual_paid_marked_at: string | null;
  pdf_r2_key: string | null;
  email_sent_to: string | null;
  sent_at: string | null;
  paid_at: string | null;
  voided_at: string | null;
  notes: string | null;
  internal_notes: string | null;
  items: InvoiceItem[];
}

interface WiseTx {
  id: string;
  reference: string | null;
  sender_name: string | null;
  amount: number;
  currency: string;
  occurred_at: string;
  matched_invoice_id: string | null;
}

interface ClientInfo {
  id: string;
  display_name: string;
  stripe_customer_id: string;
}

interface ProjectInfo {
  id: string;
  name: string;
}

interface DraftLineItemInput {
  item_name: string;
  description: string;
  quantity: string;
  unit_price: string;
  category: string;
  provider: string;
  model: string;
}

export function InvoiceDetailPage() {
  const { id } = useParams();
  const [invoice, setInvoice] = useState<InvoiceDetail | null>(null);
  const [clientInfo, setClientInfo] = useState<ClientInfo | null>(null);
  const [projectInfo, setProjectInfo] = useState<ProjectInfo | null>(null);
  const [wiseMatch, setWiseMatch] = useState<WiseTx | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyAction, setBusyAction] = useState<
    'send' | 'void' | 'pdf' | 'sync' | 'manualPaid' | null
  >(null);
  const [manualPaidReason, setManualPaidReason] = useState('');
  const [editingDraft, setEditingDraft] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftCurrency, setDraftCurrency] = useState('USD');
  const [draftIssueDate, setDraftIssueDate] = useState('');
  const [draftDueDate, setDraftDueDate] = useState('');
  const [draftPeriodStart, setDraftPeriodStart] = useState('');
  const [draftPeriodEnd, setDraftPeriodEnd] = useState('');
  const [draftTaxAmount, setDraftTaxAmount] = useState('0');
  const [draftNotes, setDraftNotes] = useState('');
  const [draftInternalNotes, setDraftInternalNotes] = useState('');
  const [draftItems, setDraftItems] = useState<DraftLineItemInput[]>([]);

  useEffect(() => {
    if (!id) return;
    void loadInvoice(id);
  }, [id]);

  async function loadInvoice(invoiceId: string): Promise<void> {
    setError(null);
    try {
      const data = await apiGet<InvoiceDetail>(`/api/invoices/${invoiceId}`);
      setInvoice(data);

      const [client, project] = await Promise.all([
        apiGet<ClientInfo>(`/api/clients/${data.client_id}`).catch(() => null),
        apiGet<ProjectInfo>(`/api/projects/${data.project_id}`).catch(() => null),
      ]);
      setClientInfo(client);
      setProjectInfo(project);

      if (data.status === 'paid' || data.payment_reference) {
        try {
          const txs = await apiGet<WiseTx[]>('/api/wise/transactions?limit=100');
          const match = txs.find(
            (tx) =>
              tx.matched_invoice_id === data.id ||
              (tx.reference && data.payment_reference && tx.reference === data.payment_reference),
          );
          setWiseMatch(match ?? null);
        } catch {
          setWiseMatch(null);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load invoice');
    }
  }

  async function sendInvoice(): Promise<void> {
    if (!id) return;
    setBusyAction('send');
    setError(null);
    try {
      await apiPost(`/api/invoices/${id}/send`, {}, { successMessage: 'Invoice sent' });
      await loadInvoice(id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send invoice');
    } finally {
      setBusyAction(null);
    }
  }

  async function voidInvoice(): Promise<void> {
    if (!id) return;
    setBusyAction('void');
    setError(null);
    try {
      await apiPost(`/api/invoices/${id}/void`, {}, { successMessage: 'Invoice voided' });
      await loadInvoice(id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to void invoice');
    } finally {
      setBusyAction(null);
    }
  }

  async function generatePdf(): Promise<void> {
    if (!id) return;
    setBusyAction('pdf');
    setError(null);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL || ''}/api/invoices/${id}/generate-pdf`,
        {
          method: 'POST',
          credentials: 'include',
        },
      );
      if (!response.ok) {
        const body = (await response.json().catch(() => null)) as { error?: string } | null;
        throw new Error(body?.error ?? `Failed (${response.status})`);
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate PDF');
    } finally {
      setBusyAction(null);
    }
  }

  async function syncStripeStatus(): Promise<void> {
    if (!id) return;
    setBusyAction('sync');
    setError(null);
    try {
      await apiPost(`/api/invoices/${id}/sync-payment-status`, {}, { silentSuccess: true });
      await loadInvoice(id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to sync Stripe status');
    } finally {
      setBusyAction(null);
    }
  }

  async function markPaidManual(): Promise<void> {
    if (!id) return;
    setBusyAction('manualPaid');
    setError(null);
    try {
      await apiPost(
        `/api/invoices/${id}/mark-paid-manual`,
        { reason: manualPaidReason.trim() || undefined },
        { successMessage: 'Invoice marked as paid (manual)' },
      );
      setManualPaidReason('');
      await loadInvoice(id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to mark invoice paid manually');
    } finally {
      setBusyAction(null);
    }
  }

  function hydrateDraftForm(data: InvoiceDetail): void {
    setDraftCurrency(data.currency.toUpperCase());
    setDraftIssueDate(toDateInput(data.issue_date));
    setDraftDueDate(toDateInput(data.due_date));
    setDraftPeriodStart(data.period_start ? toDateInput(data.period_start) : '');
    setDraftPeriodEnd(data.period_end ? toDateInput(data.period_end) : '');
    setDraftTaxAmount(String(data.tax_amount));
    setDraftNotes(data.notes ?? '');
    setDraftInternalNotes(data.internal_notes ?? '');
    setDraftItems(
      data.items.map((item) => ({
        item_name: item.item_name ?? '',
        description: item.description,
        quantity: String(item.quantity),
        unit_price: String(item.unit_price),
        category: item.category ?? '',
        provider: item.provider ?? '',
        model: item.model ?? '',
      })),
    );
  }

  async function saveDraftChanges(): Promise<void> {
    if (!id || !invoice) return;

    const parsedTax = Number(draftTaxAmount);
    if (Number.isNaN(parsedTax) || parsedTax < 0) {
      setError('Tax amount must be a non-negative number');
      return;
    }

    if (!draftIssueDate || !draftDueDate) {
      setError('Issue date and due date are required');
      return;
    }

    if (draftItems.length === 0) {
      setError('At least one line item is required');
      return;
    }

    const normalizedItems: Array<{
      item_name?: string;
      description: string;
      quantity: number;
      unit_price: number;
      category?: string;
      provider?: string;
      model?: string;
    }> = [];

    for (let index = 0; index < draftItems.length; index += 1) {
      const item = draftItems[index];
      if (!item) continue;

      const qty = Number(item.quantity);
      const unitPrice = Number(item.unit_price);
      if (!item.description.trim()) {
        setError(`Line item ${index + 1} description is required`);
        return;
      }
      if (Number.isNaN(qty) || qty <= 0) {
        setError(`Line item ${index + 1} quantity must be > 0`);
        return;
      }
      if (Number.isNaN(unitPrice) || unitPrice < 0) {
        setError(`Line item ${index + 1} unit price must be >= 0`);
        return;
      }

      normalizedItems.push({
        item_name: item.item_name.trim() || undefined,
        description: item.description.trim(),
        quantity: qty,
        unit_price: unitPrice,
        category: item.category.trim() || undefined,
        provider: item.provider.trim() || undefined,
        model: item.model.trim() || undefined,
      });
    }

    setSavingDraft(true);
    setError(null);
    try {
      const updated = await apiPatch<InvoiceDetail>(`/api/invoices/${id}`, {
        currency: draftCurrency.toLowerCase(),
        issue_date: fromDateInput(draftIssueDate),
        due_date: fromDateInput(draftDueDate),
        period_start: draftPeriodStart ? fromDateInput(draftPeriodStart) : null,
        period_end: draftPeriodEnd ? fromDateInput(draftPeriodEnd) : null,
        tax_amount: parsedTax,
        notes: draftNotes.trim() || null,
        internal_notes: draftInternalNotes.trim() || null,
        line_items: normalizedItems,
      });

      setInvoice(updated);
      hydrateDraftForm(updated);
      setEditingDraft(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save draft changes');
    } finally {
      setSavingDraft(false);
    }
  }

  const canSend = invoice?.status === 'draft';
  const canVoid =
    invoice?.status === 'draft' || invoice?.status === 'sent' || invoice?.status === 'overdue';
  const canSyncStripe = invoice?.status === 'sent' && Boolean(invoice.stripe_payment_link_url);
  const canMarkManualPaid = invoice?.status === 'sent' || invoice?.status === 'overdue';

  useEffect(() => {
    if (invoice && invoice.status === 'draft') {
      hydrateDraftForm(invoice);
    }
  }, [invoice]);

  const timeline = useMemo(() => {
    if (!invoice) return [];
    const events: Array<{ date: string; label: string; detail?: string }> = [];
    events.push({ date: invoice.issue_date, label: 'Created' });
    if (invoice.sent_at)
      events.push({
        date: invoice.sent_at,
        label: 'Sent',
        detail: invoice.email_sent_to ? `to ${invoice.email_sent_to}` : undefined,
      });
    if (invoice.paid_at) {
      if (invoice.payment_method === 'manual') {
        const parts = [
          invoice.manual_paid_marked_by ? `by ${invoice.manual_paid_marked_by}` : null,
          invoice.manual_paid_reason ? `reason: ${invoice.manual_paid_reason}` : null,
        ].filter(Boolean);
        events.push({
          date: invoice.manual_paid_marked_at ?? invoice.paid_at,
          label: 'Marked Paid (Manual)',
          detail: parts.length > 0 ? parts.join(' · ') : undefined,
        });
      } else {
        events.push({ date: invoice.paid_at, label: 'Paid' });
      }
    }
    if (invoice.voided_at) events.push({ date: invoice.voided_at, label: 'Voided' });
    return events.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }, [invoice]);

  if (!id) return <p className="text-sm text-red-600">Missing invoice id.</p>;

  return (
    <div>
      <div className="flex items-center justify-between gap-3">
        <Link to="/invoices" className="text-sm text-brand-700 hover:underline">
          &larr; Invoices
        </Link>
        <button
          type="button"
          onClick={() => {
            void loadInvoice(id);
          }}
          className="rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-700 hover:bg-slate-100"
        >
          Refresh
        </button>
      </div>

      {error ? <p className="mt-3 text-sm text-red-600">{error}</p> : null}

      {invoice ? (
        <>
          <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h1 className="text-xl font-semibold text-slate-900">{invoice.invoice_number}</h1>
                <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-slate-600">
                  <StatusBadge status={invoice.status} />
                  {projectInfo ? (
                    <>
                      <span>&middot;</span>
                      <Link
                        to={`/projects/${projectInfo.id}`}
                        className="text-brand-700 hover:underline"
                      >
                        {projectInfo.name}
                      </Link>
                    </>
                  ) : null}
                  {clientInfo ? (
                    <>
                      <span>&middot;</span>
                      <Link
                        to={`/clients/${clientInfo.id}`}
                        className="text-brand-700 hover:underline"
                      >
                        {clientInfo.display_name}
                      </Link>
                    </>
                  ) : null}
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => {
                    void generatePdf();
                  }}
                  disabled={busyAction !== null}
                  className="rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-50"
                >
                  {busyAction === 'pdf' ? 'Generating...' : 'Generate PDF'}
                </button>
                {canSyncStripe ? (
                  <button
                    type="button"
                    onClick={() => {
                      void syncStripeStatus();
                    }}
                    disabled={busyAction !== null}
                    className="rounded-md border border-blue-300 px-3 py-2 text-sm font-medium text-blue-700 hover:bg-blue-50 disabled:opacity-50"
                  >
                    {busyAction === 'sync' ? 'Syncing...' : 'Sync Stripe'}
                  </button>
                ) : null}
                {canMarkManualPaid ? (
                  <button
                    type="button"
                    onClick={() => {
                      void markPaidManual();
                    }}
                    disabled={busyAction !== null}
                    className="rounded-md border border-emerald-300 px-3 py-2 text-sm font-medium text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
                  >
                    {busyAction === 'manualPaid' ? 'Marking...' : 'Mark Paid (Cash)'}
                  </button>
                ) : null}
                {canSend ? (
                  <button
                    type="button"
                    onClick={() => {
                      if (invoice?.status !== 'draft') return;
                      if (editingDraft) {
                        hydrateDraftForm(invoice);
                      }
                      setEditingDraft((prev) => !prev);
                    }}
                    disabled={busyAction !== null || savingDraft}
                    className="rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-50"
                  >
                    {editingDraft ? 'Cancel Edit' : 'Edit Draft'}
                  </button>
                ) : null}
                {canSend && editingDraft ? (
                  <button
                    type="button"
                    onClick={() => {
                      void saveDraftChanges();
                    }}
                    disabled={busyAction !== null || savingDraft}
                    className="rounded-md border border-brand-300 px-3 py-2 text-sm font-medium text-brand-700 hover:bg-brand-50 disabled:opacity-50"
                  >
                    {savingDraft ? 'Saving...' : 'Save Draft Changes'}
                  </button>
                ) : null}
                {canSend ? (
                  <button
                    type="button"
                    onClick={() => {
                      void sendInvoice();
                    }}
                    disabled={busyAction !== null}
                    className="rounded-md bg-brand-600 px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
                  >
                    {busyAction === 'send' ? 'Sending...' : 'Send Invoice'}
                  </button>
                ) : null}
                {canVoid ? (
                  <button
                    type="button"
                    onClick={() => {
                      void voidInvoice();
                    }}
                    disabled={busyAction !== null}
                    className="rounded-md border border-rose-300 px-3 py-2 text-sm font-medium text-rose-700 hover:bg-rose-50 disabled:opacity-50"
                  >
                    {busyAction === 'void' ? 'Voiding...' : 'Void Invoice'}
                  </button>
                ) : null}
              </div>
            </div>

            <div className="mt-4 grid gap-3 md:grid-cols-3">
              <InfoCard title="Subtotal" value={formatMoney(invoice.subtotal, invoice.currency)} />
              <InfoCard title="Tax" value={formatMoney(invoice.tax_amount, invoice.currency)} />
              <InfoCard
                title="Total"
                value={formatMoney(invoice.total, invoice.currency)}
                highlight
              />
            </div>

            <div className="mt-3 grid gap-3 md:grid-cols-3">
              <InfoCard title="Issue Date" value={shortDate(invoice.issue_date)} />
              <InfoCard title="Due Date" value={shortDate(invoice.due_date)} />
              <InfoCard
                title="Payment Status"
                value={invoice.paid_at ? `Paid ${shortDateTime(invoice.paid_at)}` : 'Unpaid'}
              />
            </div>

            {invoice.period_start || invoice.period_end ? (
              <div className="mt-3">
                <InfoCard
                  title="Billing Period"
                  value={`${invoice.period_start ? shortDate(invoice.period_start) : '?'} — ${invoice.period_end ? shortDate(invoice.period_end) : '?'}`}
                />
              </div>
            ) : null}

            {invoice.status === 'draft' && editingDraft ? (
              <div className="mt-4 rounded-md border border-slate-200 bg-white p-4">
                <h3 className="text-sm font-semibold text-slate-800">Edit Draft Invoice</h3>
                <div className="mt-3 grid gap-3 md:grid-cols-3">
                  <label className="text-xs font-medium text-slate-600">
                    Currency
                    <input
                      value={draftCurrency}
                      onChange={(event) => {
                        setDraftCurrency(event.target.value.toUpperCase());
                      }}
                      className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1 text-sm"
                    />
                  </label>
                  <label className="text-xs font-medium text-slate-600">
                    Issue Date
                    <input
                      type="date"
                      value={draftIssueDate}
                      onChange={(event) => {
                        setDraftIssueDate(event.target.value);
                      }}
                      className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1 text-sm"
                    />
                  </label>
                  <label className="text-xs font-medium text-slate-600">
                    Due Date
                    <input
                      type="date"
                      value={draftDueDate}
                      onChange={(event) => {
                        setDraftDueDate(event.target.value);
                      }}
                      className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1 text-sm"
                    />
                  </label>
                  <label className="text-xs font-medium text-slate-600">
                    Period Start
                    <input
                      type="date"
                      value={draftPeriodStart}
                      onChange={(event) => {
                        setDraftPeriodStart(event.target.value);
                      }}
                      className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1 text-sm"
                    />
                  </label>
                  <label className="text-xs font-medium text-slate-600">
                    Period End
                    <input
                      type="date"
                      value={draftPeriodEnd}
                      onChange={(event) => {
                        setDraftPeriodEnd(event.target.value);
                      }}
                      className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1 text-sm"
                    />
                  </label>
                  <label className="text-xs font-medium text-slate-600">
                    Tax Amount
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={draftTaxAmount}
                      onChange={(event) => {
                        setDraftTaxAmount(event.target.value);
                      }}
                      className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1 text-sm"
                    />
                  </label>
                </div>

                <div className="mt-3 grid gap-3 md:grid-cols-2">
                  <label className="text-xs font-medium text-slate-600">
                    Customer Notes
                    <textarea
                      value={draftNotes}
                      onChange={(event) => {
                        setDraftNotes(event.target.value);
                      }}
                      rows={2}
                      className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1 text-sm"
                    />
                  </label>
                  <label className="text-xs font-medium text-slate-600">
                    Internal Notes
                    <textarea
                      value={draftInternalNotes}
                      onChange={(event) => {
                        setDraftInternalNotes(event.target.value);
                      }}
                      rows={2}
                      className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1 text-sm"
                    />
                  </label>
                </div>

                <div className="mt-4">
                  <div className="flex items-center justify-between">
                    <h4 className="text-xs font-semibold text-slate-700">Line Items</h4>
                    <button
                      type="button"
                      onClick={() => {
                        setDraftItems((prev) => [
                          ...prev,
                          {
                            item_name: '',
                            description: '',
                            quantity: '1',
                            unit_price: '0',
                            category: '',
                            provider: '',
                            model: '',
                          },
                        ]);
                      }}
                      className="rounded-md border border-slate-300 px-2 py-1 text-xs text-slate-700 hover:bg-slate-100"
                    >
                      Add Line
                    </button>
                  </div>

                  <div className="mt-2 space-y-2">
                    {draftItems.map((item, index) => (
                      <div key={index} className="rounded-md border border-slate-200 p-2">
                        <div className="grid gap-2 md:grid-cols-4">
                          <input
                            value={item.item_name}
                            onChange={(event) => {
                              setDraftItems((prev) =>
                                prev.map((current, currentIndex) =>
                                  currentIndex === index
                                    ? { ...current, item_name: event.target.value }
                                    : current,
                                ),
                              );
                            }}
                            placeholder="Item name"
                            className="rounded-md border border-slate-300 px-2 py-1 text-xs"
                          />
                          <input
                            value={item.description}
                            onChange={(event) => {
                              setDraftItems((prev) =>
                                prev.map((current, currentIndex) =>
                                  currentIndex === index
                                    ? { ...current, description: event.target.value }
                                    : current,
                                ),
                              );
                            }}
                            placeholder="Description"
                            className="rounded-md border border-slate-300 px-2 py-1 text-xs md:col-span-2"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              setDraftItems((prev) => prev.filter((_, i) => i !== index));
                            }}
                            className="rounded-md border border-rose-300 px-2 py-1 text-xs text-rose-700 hover:bg-rose-50"
                          >
                            Remove
                          </button>
                          <input
                            type="number"
                            min="0.0001"
                            step="0.0001"
                            value={item.quantity}
                            onChange={(event) => {
                              setDraftItems((prev) =>
                                prev.map((current, currentIndex) =>
                                  currentIndex === index
                                    ? { ...current, quantity: event.target.value }
                                    : current,
                                ),
                              );
                            }}
                            placeholder="Qty"
                            className="rounded-md border border-slate-300 px-2 py-1 text-xs"
                          />
                          <input
                            type="number"
                            min="0"
                            step="0.0001"
                            value={item.unit_price}
                            onChange={(event) => {
                              setDraftItems((prev) =>
                                prev.map((current, currentIndex) =>
                                  currentIndex === index
                                    ? { ...current, unit_price: event.target.value }
                                    : current,
                                ),
                              );
                            }}
                            placeholder="Unit price"
                            className="rounded-md border border-slate-300 px-2 py-1 text-xs"
                          />
                          <input
                            value={item.category}
                            onChange={(event) => {
                              setDraftItems((prev) =>
                                prev.map((current, currentIndex) =>
                                  currentIndex === index
                                    ? { ...current, category: event.target.value }
                                    : current,
                                ),
                              );
                            }}
                            placeholder="Category"
                            className="rounded-md border border-slate-300 px-2 py-1 text-xs"
                          />
                          <input
                            value={item.provider}
                            onChange={(event) => {
                              setDraftItems((prev) =>
                                prev.map((current, currentIndex) =>
                                  currentIndex === index
                                    ? { ...current, provider: event.target.value }
                                    : current,
                                ),
                              );
                            }}
                            placeholder="Provider"
                            className="rounded-md border border-slate-300 px-2 py-1 text-xs"
                          />
                          <input
                            value={item.model}
                            onChange={(event) => {
                              setDraftItems((prev) =>
                                prev.map((current, currentIndex) =>
                                  currentIndex === index
                                    ? { ...current, model: event.target.value }
                                    : current,
                                ),
                              );
                            }}
                            placeholder="Model"
                            className="rounded-md border border-slate-300 px-2 py-1 text-xs md:col-span-2"
                          />
                          <p className="text-right text-xs font-medium text-slate-600">
                            {formatMoney(
                              (Number(item.quantity) || 0) * (Number(item.unit_price) || 0),
                              draftCurrency,
                            )}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : null}

            {canMarkManualPaid ? (
              <div className="mt-3 rounded-md border border-emerald-200 bg-emerald-50 p-3">
                <label className="block text-xs font-medium text-emerald-800">
                  Manual payment note (optional)
                </label>
                <textarea
                  value={manualPaidReason}
                  onChange={(event) => {
                    setManualPaidReason(event.target.value);
                  }}
                  maxLength={500}
                  rows={2}
                  className="mt-2 w-full rounded-md border border-emerald-200 bg-white px-2 py-1 text-sm text-slate-700"
                  placeholder="e.g. Cash settled in person, receipt #A102"
                />
                <p className="mt-1 text-[11px] text-emerald-700">
                  This note will be recorded in timeline/audit.
                </p>
              </div>
            ) : null}
          </div>

          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <section className="rounded-lg border border-slate-200 p-4">
              <h2 className="text-base font-semibold">Payment Details</h2>
              <div className="mt-3 space-y-2 text-sm">
                <DetailRow label="Payment Reference" value={invoice.payment_reference ?? '-'} />
                <DetailRow label="Sent to" value={invoice.email_sent_to ?? '-'} />
                <DetailRow
                  label="Sent at"
                  value={invoice.sent_at ? shortDateTime(invoice.sent_at) : '-'}
                />
                <DetailRow label="PDF" value={invoice.pdf_r2_key ? 'Generated' : 'Not generated'} />

                {invoice.stripe_payment_link_url ? (
                  <div className="mt-2 rounded-md border border-blue-200 bg-blue-50 p-3">
                    <p className="text-xs font-medium text-blue-800">Stripe Payment Link</p>
                    <a
                      href={invoice.stripe_payment_link_url}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-1 block text-sm text-blue-700 underline break-all"
                    >
                      {invoice.stripe_payment_link_url}
                    </a>
                    {canSyncStripe ? (
                      <p className="mt-2 text-xs text-blue-800">
                        If Stripe already shows payment success, use Sync Stripe to pull the latest
                        status when the webhook has not updated this invoice yet.
                      </p>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </section>

            <section className="rounded-lg border border-slate-200 p-4">
              <h2 className="text-base font-semibold">Bank Transfer (Wise)</h2>
              {wiseMatch ? (
                <div className="mt-3 space-y-2 text-sm">
                  <DetailRow label="Transfer ID" value={wiseMatch.id} />
                  <DetailRow label="Sender" value={wiseMatch.sender_name ?? '-'} />
                  <DetailRow
                    label="Amount"
                    value={formatMoney(wiseMatch.amount, wiseMatch.currency)}
                  />
                  <DetailRow label="Reference" value={wiseMatch.reference ?? '-'} />
                  <DetailRow label="Date" value={shortDateTime(wiseMatch.occurred_at)} />
                  <div className="mt-2 rounded-md border border-emerald-200 bg-emerald-50 p-2 text-xs text-emerald-800">
                    Matched via payment reference
                  </div>
                </div>
              ) : (
                <p className="mt-3 text-sm text-slate-500">
                  {invoice.status === 'paid'
                    ? 'No matching Wise transfer found — may have been paid via Stripe.'
                    : 'No bank transfer matched yet.'}
                </p>
              )}
              <Link
                to="/reconciliation"
                className="mt-3 inline-block text-sm text-brand-700 underline"
              >
                Go to Reconciliation
              </Link>
            </section>
          </div>

          {invoice.notes || invoice.internal_notes ? (
            <div className="mt-4 grid gap-4 md:grid-cols-2">
              {invoice.notes ? (
                <section className="rounded-lg border border-slate-200 p-4">
                  <h2 className="text-sm font-semibold text-slate-700">Customer Notes</h2>
                  <p className="mt-2 text-sm text-slate-600 whitespace-pre-wrap">{invoice.notes}</p>
                </section>
              ) : null}
              {invoice.internal_notes ? (
                <section className="rounded-lg border border-slate-200 p-4">
                  <h2 className="text-sm font-semibold text-slate-700">Internal Notes</h2>
                  <p className="mt-2 text-sm text-slate-600 whitespace-pre-wrap">
                    {invoice.internal_notes}
                  </p>
                </section>
              ) : null}
            </div>
          ) : null}

          <section className="mt-6">
            <h2 className="text-lg font-semibold">Line Items</h2>
            <div className="mt-2 overflow-x-auto rounded-lg border border-slate-200">
              <table className="min-w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50 text-left text-slate-600">
                    <th className="px-3 py-2">Item</th>
                    <th className="px-3 py-2 text-right">Qty</th>
                    <th className="px-3 py-2 text-right">Unit Price</th>
                    <th className="px-3 py-2 text-right">Amount</th>
                    {invoice.items.some((i) => i.provider || i.model) ? (
                      <th className="px-3 py-2">Provider / Model</th>
                    ) : null}
                  </tr>
                </thead>
                <tbody>
                  {invoice.items.map((item) => (
                    <tr key={item.id} className="border-b border-slate-100 last:border-0">
                      <td className="px-3 py-2">
                        {item.item_name ? <p className="font-medium">{item.item_name}</p> : null}
                        <p className={item.item_name ? 'text-xs text-slate-500' : ''}>
                          {item.description}
                        </p>
                      </td>
                      <td className="px-3 py-2 text-right">{item.quantity}</td>
                      <td className="px-3 py-2 text-right">
                        {formatMoney(item.unit_price, invoice.currency)}
                      </td>
                      <td className="px-3 py-2 text-right font-medium">
                        {formatMoney(item.amount, invoice.currency)}
                      </td>
                      {invoice.items.some((i) => i.provider || i.model) ? (
                        <td className="px-3 py-2 text-xs text-slate-500">
                          {[item.provider, item.model].filter(Boolean).join(' / ') || '-'}
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t border-slate-200 bg-slate-50">
                    <td colSpan={3} className="px-3 py-2 text-right text-slate-600">
                      Subtotal
                    </td>
                    <td className="px-3 py-2 text-right font-medium">
                      {formatMoney(invoice.subtotal, invoice.currency)}
                    </td>
                    {invoice.items.some((i) => i.provider || i.model) ? <td /> : null}
                  </tr>
                  {invoice.tax_amount > 0 ? (
                    <tr className="bg-slate-50">
                      <td colSpan={3} className="px-3 py-2 text-right text-slate-600">
                        Tax
                      </td>
                      <td className="px-3 py-2 text-right font-medium">
                        {formatMoney(invoice.tax_amount, invoice.currency)}
                      </td>
                      {invoice.items.some((i) => i.provider || i.model) ? <td /> : null}
                    </tr>
                  ) : null}
                  <tr className="bg-slate-50">
                    <td colSpan={3} className="px-3 py-2 text-right font-semibold text-slate-900">
                      Total
                    </td>
                    <td className="px-3 py-2 text-right font-semibold text-slate-900">
                      {formatMoney(invoice.total, invoice.currency)}
                    </td>
                    {invoice.items.some((i) => i.provider || i.model) ? <td /> : null}
                  </tr>
                </tfoot>
              </table>
            </div>
          </section>

          {timeline.length > 0 ? (
            <section className="mt-6">
              <h2 className="text-lg font-semibold">Timeline</h2>
              <div className="mt-2 space-y-0">
                {timeline.map((event, idx) => (
                  <div
                    key={idx}
                    className="flex items-start gap-3 border-l-2 border-slate-200 py-2 pl-4"
                  >
                    <div className="-ml-[21px] mt-0.5 h-2.5 w-2.5 rounded-full bg-brand-500" />
                    <div>
                      <p className="text-sm font-medium text-slate-900">{event.label}</p>
                      <p className="text-xs text-slate-500">
                        {shortDateTime(event.date)}
                        {event.detail ? ` · ${event.detail}` : ''}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

function InfoCard(props: { title: string; value: string; highlight?: boolean }) {
  return (
    <div className="rounded-md border border-slate-200 bg-white p-3 text-sm">
      <p className="text-slate-500">{props.title}</p>
      <p
        className={`mt-1 ${props.highlight ? 'text-lg font-semibold' : 'font-medium'} text-slate-900`}
      >
        {props.value}
      </p>
    </div>
  );
}

function DetailRow(props: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-slate-500">{props.label}</span>
      <span className="font-medium text-slate-900 text-right">{props.value}</span>
    </div>
  );
}

function StatusBadge(props: { status: InvoiceStatus }) {
  const styles: Record<InvoiceStatus, string> = {
    paid: 'bg-emerald-50 text-emerald-700',
    sent: 'bg-blue-50 text-blue-700',
    overdue: 'bg-amber-50 text-amber-700',
    void: 'bg-slate-100 text-slate-600',
    draft: 'bg-purple-50 text-purple-700',
  };
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${styles[props.status]}`}>
      {props.status}
    </span>
  );
}

function formatMoney(value: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: 2,
  }).format(value);
}

function shortDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toISOString().slice(0, 10);
}

function shortDateTime(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toISOString().slice(0, 16).replace('T', ' ');
}

function toDateInput(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return '';
  }
  return d.toISOString().slice(0, 10);
}

function fromDateInput(value: string): string {
  return `${value}T00:00:00.000Z`;
}
